// ============================================================================
// PROJECTS DASHBOARD PAGE
// List view for managing projects - renders inside AdminLayout sidebar
// ============================================================================

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Users, FileText, FolderOpen, ChevronRight, ChevronLeft, Lock, ShieldAlert, X } from 'lucide-react';
import { useProjects, BreakGlassContext } from '../hooks/useProjects';
import { useAuth } from '../contexts/AuthContext';
import { CreateProjectModal } from '../components/CreateProjectModal';
import { EmptyState } from '../components/EmptyState';
import { BreakGlassModal } from '../components/common/BreakGlassModal';

const PAGE_SIZE = 10;

const ProjectsDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const isSystemAdmin = hasRole('system_admin');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'archived'>('active');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [breakGlassCtx, setBreakGlassCtx] = useState<BreakGlassContext | null>(null);
  const [breakGlassModalOpen, setBreakGlassModalOpen] = useState(false);

  const { data: projects, isLoading, error } = useProjects({
    status: filterStatus === 'all' ? undefined : filterStatus,
    breakGlass: breakGlassCtx,
  });

  const filteredProjects = useMemo(() => {
    if (!projects) return [];
    let filtered = projects.filter(p => p && p.projectId && p.name);
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p =>
        p.name.toLowerCase().includes(query) ||
        p.description.toLowerCase().includes(query)
      );
    }
    filtered.sort((a, b) => {
      const aTime = new Date(a.lastActivityAt).getTime();
      const bTime = new Date(b.lastActivityAt).getTime();
      return bTime - aTime;
    });
    return filtered;
  }, [projects, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredProjects.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const pagedProjects = filteredProjects.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const formatDistanceToNow = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months}mo ago`;
    const years = Math.floor(months / 12);
    return `${years}y ago`;
  };

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-6">

      {/* Page Header */}
      <header className="flex items-center justify-between border-b border-slate-200 pb-6">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
            <FolderOpen size={22} className="text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-navy">Projects</h1>
            <p className="text-sm text-slate-500 font-medium">Manage and facilitate your active co-creation sessions</p>
          </div>
        </div>
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="flex items-center justify-center gap-2 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm"
        >
          <Plus size={16} />
          New Project
        </button>
      </header>

      {/* Break glass active banner */}
      {breakGlassCtx && (
        <div className="mb-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl flex items-center justify-between">
          <div className="flex items-center gap-2 text-amber-800 text-sm">
            <ShieldAlert size={16} className="text-amber-500 shrink-0" />
            <span>Break glass active — viewing all projects in your organisation</span>
          </div>
          <button
            onClick={() => { setBreakGlassCtx(null); setCurrentPage(1); }}
            className="flex items-center gap-1.5 text-xs font-medium text-amber-700 hover:text-amber-900 transition-colors"
          >
            <X size={14} /> Dismiss
          </button>
        </div>
      )}

      {/* Search + Filter Bar + Table — one unit */}
      <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
        <div className="p-4 border-b border-[#e2e8f0]">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="flex-1 min-w-[280px] relative">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search projects..."
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                className="w-full pl-10 pr-4 py-2.5 border border-[#e2e8f0] rounded-xl text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
              />
            </div>
            <div className="flex items-center gap-2">
              {(['active', 'all', 'archived'] as const).map((status) => (
                <button
                  key={status}
                  onClick={() => { setFilterStatus(status); setCurrentPage(1); }}
                  className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-all ${
                    filterStatus === status
                      ? 'bg-primary/10 text-primary border border-primary/30'
                      : 'bg-white text-slate-500 border border-[#e2e8f0] hover:border-primary/30 hover:text-slate-700'
                  }`}
                >
                  {status === 'all' ? 'All' : status}
                </button>
              ))}
              {isSystemAdmin && !breakGlassCtx && (
                <>
                  <div className="w-px h-5 bg-slate-200" />
                  <button
                    onClick={() => setBreakGlassModalOpen(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-amber-600 hover:bg-amber-50 transition-colors border border-transparent hover:border-amber-200"
                  >
                    <Lock size={11} /> View all
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

      {isLoading ? (
        <div className="flex justify-center items-center h-40">
          <svg className="w-6 h-6 animate-spin text-slate-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        </div>
      ) : error ? (
        <div className="p-5 flex items-center gap-3 text-red-700 bg-red-50 border-t border-[#e2e8f0]">
          <span className="font-semibold">Error:</span> {error.message}
        </div>
      ) : filteredProjects.length === 0 ? (
        <div className="p-12">
          {projects && projects.length === 0 ? (
            <EmptyState
              icon={FolderOpen}
              title="No projects yet"
              description="Create your first project to start organizing your co-creation sessions"
              action={
                <button
                  onClick={() => setIsCreateModalOpen(true)}
                  className="flex items-center justify-center gap-2 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm"
                >
                  Create Project
                </button>
              }
            />
          ) : (
            <EmptyState
              icon={Search}
              title="No projects found"
              description="Try adjusting your search or filters"
              action={
                <button
                  onClick={() => { setSearchQuery(''); setFilterStatus('all'); setCurrentPage(1); }}
                  className="flex items-center justify-center gap-2 rounded-xl h-10 px-6 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors"
                >
                  Clear Filters
                </button>
              }
            />
          )}
        </div>
      ) : (
        <>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#e2e8f0] bg-slate-50/50">
                <th className="px-5 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-400">Project</th>
                <th className="px-5 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-400">Status</th>
                <th className="px-5 py-3 text-center text-xs font-bold uppercase tracking-wider text-slate-400">Sessions</th>
                <th className="px-5 py-3 text-center text-xs font-bold uppercase tracking-wider text-slate-400">Documents</th>
                <th className="px-5 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-400">Last Activity</th>
                <th className="px-5 py-3 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#e2e8f0]">
              {pagedProjects.map((project: any) => (
                <tr
                  key={project.projectId}
                  onClick={() => navigate(`/projects/${project.projectId}`)}
                  className="hover:bg-slate-50/60 transition-colors cursor-pointer group"
                >
                  <td className="px-5 py-4 max-w-[400px]">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-navy">{project.name}</span>
                      {project.accessLevel === 'break_glass' && (
                        <span title="Accessed via break glass"><Lock size={12} className="text-amber-500 shrink-0" /></span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 truncate">{project.description || 'No description'}</div>
                  </td>
                  <td className="px-5 py-4">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider ${
                      project.status === 'archived'
                        ? 'bg-slate-100 text-slate-600'
                        : 'bg-emerald-100 text-emerald-700'
                    }`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${
                        project.status === 'archived' ? 'bg-slate-400' : 'bg-emerald-600'
                      }`} />
                      {project.status || 'active'}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-center">
                    <div className="flex items-center justify-center gap-1.5 text-slate-600">
                      <Users size={14} className="text-primary" />
                      <span className="font-medium">{project.sessionCount || 0}</span>
                    </div>
                  </td>
                  <td className="px-5 py-4 text-center">
                    <div className="flex items-center justify-center gap-1.5 text-slate-600">
                      <FileText size={14} className="text-primary" />
                      <span className="font-medium">{project.documentCount || 0}</span>
                    </div>
                  </td>
                  <td className="px-5 py-4 text-right">
                    <span className="text-xs text-slate-400 font-medium">
                      {formatDistanceToNow(project.lastActivityAt)}
                    </span>
                  </td>
                  <td className="px-3 py-4">
                    <ChevronRight size={16} className="text-slate-300 group-hover:text-primary transition-colors" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination Footer */}
          <div className="border-t border-[#e2e8f0] px-5 py-3 flex items-center justify-between text-sm text-slate-500">
            <span>
              {filteredProjects.length === 0 ? '0 projects' : `${(safePage - 1) * PAGE_SIZE + 1}–${Math.min(safePage * PAGE_SIZE, filteredProjects.length)} of ${filteredProjects.length}`}
            </span>
            {totalPages > 1 && (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={safePage === 1}
                  className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft size={16} />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-7 h-7 rounded-lg text-xs font-medium transition-colors ${
                      page === safePage
                        ? 'bg-primary/10 text-primary border border-primary/30'
                        : 'hover:bg-slate-100 text-slate-600'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={safePage === totalPages}
                  className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            )}
          </div>
        </>
      )}
      </div>

      {/* Create Modal */}
      <CreateProjectModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={(project) => {
          setIsCreateModalOpen(false);
          navigate(`/projects/${project.projectId}`);
        }}
      />

      {/* Break Glass Modal */}
      <BreakGlassModal
        isOpen={breakGlassModalOpen}
        onClose={() => setBreakGlassModalOpen(false)}
        onConfirm={(ctx) => {
          setBreakGlassCtx(ctx);
          setBreakGlassModalOpen(false);
          setCurrentPage(1);
        }}
        action="view all projects in your organisation"
      />

      </div>
    </div>
  );
};

export default ProjectsDashboard;
